import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search as SearchIcon, Loader2 } from 'lucide-react';
import { api, fmt, type Row } from '../lib/api';

export default function Search() {
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [results, setResults] = useState<Row[]>([]);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (q.trim().length < 2) {
      setError('Type at least 2 characters');
      return;
    }
    setLoading(true);
    setError(null);
    setSubmitted(q.trim());
    try {
      const r = await api.search(q.trim());
      setResults(r.rows);
      setCapped(r.capped_at_100);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
          <SearchIcon size={20} className="inline mr-2" style={{ color: 'var(--color-primary)' }} />
          Search
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Find a row by policy number or policyholder name across all systems, carriers, and batches.
        </p>
      </div>

      <form onSubmit={handleSearch} className="card flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <label className="label" style={{ marginBottom: '4px' }}>
            Policy number or policyholder
          </label>
          <input
            type="text"
            className="input text-sm"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='e.g. "U98238146" or "Smith"'
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <SearchIcon size={14} />}
          Search
        </button>
      </form>

      {error && <div className="alert alert-error"><p className="text-sm">{error}</p></div>}

      {submitted && !loading && (
        <div>
          <p className="text-sm mb-3" style={{ color: 'var(--color-text-muted)' }}>
            {results.length === 0
              ? `No matches for "${submitted}".`
              : `${results.length} match${results.length === 1 ? '' : 'es'} for "${submitted}"${capped ? ' (capped at 100 — refine search to narrow)' : ''}.`}
          </p>
          {results.length > 0 && (
            <div className="card overflow-hidden p-0">
              <table className="table">
                <thead>
                  <tr>
                    <th>System / Carrier</th>
                    <th>Policy</th>
                    <th>Policyholder</th>
                    <th>Agent</th>
                    <th className="text-right">Net</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => {
                    const sysSlug = r.sys === 'Messer' ? 'messer' : 'empire-aca';
                    return (
                      <tr key={`${r.batch_id}-${r.row_idx}`}>
                        <td className="text-xs">
                          <span style={{ color: 'var(--color-text-muted)' }}>{r.sys} /</span>{' '}
                          {r.carrier}
                        </td>
                        <td className="font-mono text-xs">{r.policy_id}</td>
                        <td>{r.policyholder_name}</td>
                        <td>{r.agent_name}</td>
                        <td className="text-right font-mono">{fmt.money(r.net_amount)}</td>
                        <td>
                          {r.paid ? (
                            <span className="badge badge-success text-xs">Paid</span>
                          ) : (
                            <span className="badge badge-warning text-xs">Pending</span>
                          )}
                        </td>
                        <td className="text-xs">{fmt.dateOnly(r.transaction_date)}</td>
                        <td className="text-right">
                          <Link
                            to={`/${sysSlug}/batches/${r.batch_id}`}
                            className="text-sm"
                            style={{ color: 'var(--color-primary)' }}
                          >
                            Batch →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
